import { MutableRefObject, useCallback, useEffect, useRef, useState } from "react";

import { ActionIcon, Box, Flex, Group, Text } from "@mantine/core";
import { Dropzone, IMAGE_MIME_TYPE, PDF_MIME_TYPE, FileWithPath } from '@mantine/dropzone';
import { IconCircleX, IconDownload, IconFileTypePdf, IconPlus, IconUpload } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";

import { ServiceConstants } from "constant";
import { useServiceSuccess } from "hooks";
import { UserService } from "services";
import { UserResponse } from "types";
import { DateUtils } from "utils";

import classes from "./DocumentationTab.module.css";

interface DocumentItemProps {
  name: string;
  size: string;
  date: string;
  onDownload?: () => void;
  onDelete?: () => void;
}

const DocumentItem = ({ name, size, date, onDownload, onDelete }: DocumentItemProps) => (
  <Flex className={classes.documentItem} mt="md" w="100%">
    <Group className={classes.documentMeta} gap="sm" wrap="nowrap" style={{ minWidth: 0 }}>
      <IconFileTypePdf size={40} color="var(--mantine-color-primary-6)" />
      <Box style={{ minWidth: 0 }}>
        <Text size="sm" style={{ wordBreak: 'break-all' }}>{name}</Text>
        <Text size="xs" c="dimmed">{size}</Text>
      </Box>
    </Group>

    <Group className={classes.documentActions} gap="xs" wrap="nowrap">
      <Text size="xs" c="dimmed" mr="md">{date}</Text>
      <ActionIcon variant="transparent" size={28} onClick={onDownload}>
        <IconDownload size={24} color="var(--mantine-color-primary-6)" />
      </ActionIcon>
      <ActionIcon variant="transparent" size={28} onClick={onDelete}>
        <IconCircleX size={24} color="var(--mantine-color-red-6)" stroke={1.5} />
      </ActionIcon>
    </Group>
  </Flex>
);

interface DocumentationTabProps {
  user?: UserResponse;
}

const DOCUMENT_MIME_TYPES = [
  ...PDF_MIME_TYPE,
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
];

const dropzoneStyles = {
  root: {
    border: "1px dashed rgba(50, 51, 67, 0.3)",
    borderRadius: 8,
    backgroundColor: "white",
    padding: 8,
    width: "100%",
    maxWidth: 320,
  },
};

interface UploadDropzoneProps {
  accept: string[];
  hidden?: boolean;
  label: string;
  onDrop: (files: FileWithPath[]) => void | Promise<void>;
  openRef: MutableRefObject<(() => void) | null>;
}

const UploadDropzone = ({ accept, hidden = false, label, onDrop, openRef }: UploadDropzoneProps) => (
  <Dropzone
    openRef={openRef}
    onDrop={onDrop}
    maxSize={30 * 1024 * 1024}
    accept={accept}
    style={hidden ? { display: "none" } : undefined}
    styles={dropzoneStyles}
    className={classes.dropzone}
  >
    <Flex align="center" gap="xs">
      <IconUpload size={16} color="gray" />
      <Text size="sm" c="dimmed">
        {label}
      </Text>
    </Flex>
  </Dropzone>
);

const DocumentationTab = ({ user }: DocumentationTabProps) => {
  const { t } = useTranslation();
  const [coverLetters, setCoverLetters] = useState<{ id?: string; name: string; size: number; extension: string; date?: string }[]>([]);
  const [otherDocs, setOtherDocs] = useState<{ id?: string; name: string; size: number; extension: string; date?: string }[]>([]);
  
  const coverLetterRef = useRef<() => void>(null);
  const otherDocsRef = useRef<() => void>(null);

  const fetchAttachments = useCallback(() => {
    if (user?.id) {
      UserService.getUserAttachments(user.id);
    }
  }, [user?.id]);

  useEffect(() => {
    fetchAttachments();
  }, [fetchAttachments]);

  const listService = useServiceSuccess(ServiceConstants.REQUEST_USER_ATTACHMENT_LIST_ID);

  useEffect(() => {
    if (listService.isSuccess && listService.data) {
      const data = listService.data as any[];
      const covers = data.filter((d: any) => d.type === 'COVER_LETTER');
      const others = data.filter((d: any) => d.type !== 'COVER_LETTER');
      
      setCoverLetters(covers.map(c => ({ 
        id: c.id, 
        name: c.name, 
        size: c.size, 
        extension: c.extension,
        date: c.info?.createdAt 
      })));
      setOtherDocs(others.map(o => ({ 
        id: o.id, 
        name: o.name, 
        size: o.size, 
        extension: o.extension,
        date: o.info?.createdAt 
      })));
    }
  }, [listService.isSuccess, listService.data]);

  const handleCoverLetterDrop = async (files: FileWithPath[]) => {
    if (!user?.id || files.length === 0) return;
    
    const file = files[0];
    try {
      await UserService.uploadCoverLetter(user.id, file);
      fetchAttachments();
    } catch (err) {
      console.error("Upload failed", err);
    }
  };

  const handleRemoveCoverLetter = async (index: number) => {
    const doc = coverLetters[index];
    if (doc.id) {
      try {
        await UserService.deleteUserAttachment(doc.id);
        fetchAttachments();
      } catch (err) {
        console.error("Delete failed", err);
      }
    }
  };

  const handleDownload = (doc: { id?: string; name: string; extension: string }) => {
    if (doc.id) {
      UserService.downloadUserAttachment(doc.id, doc.name, doc.extension);
    }
  };

  const handleRemoveOtherDoc = async (index: number) => {
    const doc = otherDocs[index];
    if (doc.id) {
      try {
        await UserService.deleteUserAttachment(doc.id);
        fetchAttachments();
      } catch (err) {
        console.error("Delete failed", err);
      }
    }
  };

  const handleOtherDocsDrop = async (files: FileWithPath[]) => {
    if (!user?.id || files.length === 0) return;

    const file = files[0];
    try {
      await UserService.uploadOtherAttachment(user.id, file);
      fetchAttachments();
    } catch (err) {
      console.error("Upload failed", err);
    }
  };

  return (
    <Box className={classes.layout}>
      {/* Leva kolona - Propratno pismo */}
      <Box className={classes.section}>
        <Text size="lg" fw={600} c="var(--mantine-color-primary-6)" mb="md">
          {t("profilePage.documentation.coverLetter.title")}
        </Text>

        {coverLetters.length > 0 ? (
          coverLetters.map((file, index) => (
            <DocumentItem
              key={`cover-${index}`}
              name={file.name}
              size={`${(file.size / 1024 / 1024).toFixed(2)} MB`}
              date={DateUtils.formatDateDisplay(file.date || new Date())}
              onDelete={() => handleRemoveCoverLetter(index)}
              onDownload={() => handleDownload(file)}
            />
          ))
        ) : (
          <UploadDropzone
            openRef={coverLetterRef}
            onDrop={handleCoverLetterDrop}
            accept={[...PDF_MIME_TYPE, ...IMAGE_MIME_TYPE]}
            label={t("profilePage.documentation.coverLetter.add")}
          />
        )}
      </Box>

      {/* Srednja linija separator */}
      <Box className={classes.separator} />

      {/* Desna kolona - Ostala dokumentacija */}
      <Box className={classes.section}>
        <Group align="center" gap="sm" mb="md">
          <Text size="lg" fw={600} c="var(--mantine-color-yellow-8)">
            {t("profilePage.documentation.other.title")}
          </Text>
          <ActionIcon 
            variant="filled" 
            color="var(--mantine-color-primary-6)" 
            size="sm" 
            radius="xl"
            onClick={() => otherDocsRef.current?.()}
          >
            <IconPlus size={14} stroke={3} />
          </ActionIcon>
        </Group>

        {otherDocs.length > 0 ? (
          otherDocs.map((file, index) => (
            <DocumentItem
              key={`other-${index}`}
              name={file.name}
              size={`${(file.size / 1024 / 1024).toFixed(2)} MB`}
              date={DateUtils.formatDateDisplay(file.date || new Date())}
              onDelete={() => handleRemoveOtherDoc(index)}
              onDownload={() => handleDownload(file)}
            />
          ))
        ) : null}

        <UploadDropzone
          openRef={otherDocsRef}
          onDrop={handleOtherDocsDrop}
          accept={DOCUMENT_MIME_TYPES}
          hidden={otherDocs.length > 0}
          label={t("profilePage.documentation.other.add")}
        />
      </Box>
    </Box>
  );
};

export default DocumentationTab;
